import {
  IClientType,
  ICombinationAll,
  IKeyword,
  IQuestionType,
  ISectionAll,
  valueLabelPair
} from "@/types/SPTypes.tsx";
import { RADIO_OPTIONS } from "./enums.js";
import { isCombination, isGuestCombination } from "@/types/typeGuards.ts";
import { RootState } from "@/reducers/rootReducer.ts";

export const keywordOptions = (keywords: IKeyword[]) => {
  return keywords.map(keyword => ({
    value: keyword.ID,
    label: keyword.keyword,
    section: keyword.section,
    sectionTitle: keyword.sectionTitle
  }));
};

export function filterAssignCombinations(
  section: valueLabelPair<number> | null,
  clientType: valueLabelPair<number> | null,
  questionType: valueLabelPair<number> | null,
  sectionType: string | null,
  data: RootState["application"]
) {
  let filteredSections = data.sections;

  let combinations: ICombinationAll[] = data.combinations;

  // Filter based on 'sectionType'
  if (sectionType && sectionType !== RADIO_OPTIONS.ALL) {
    if (data.isLoggedIn) {
      combinations = combinations.filter(combination => {
        if (!isCombination(combination)) return false;

        if (sectionType === RADIO_OPTIONS.OPERATION) {
          return combination.section?.sectionType === "Operation";
        } else if (sectionType === RADIO_OPTIONS.SYSTEM) {
          return combination.section?.sectionType === "System";
        }
        return true;
      });
    } else {
      // Filter sections based on what exists in the combinations array
      const filteredSections = data.sections.filter((section: ISectionAll) =>
        combinations.some(
          combination =>
            isGuestCombination(combination) &&
            combination.sectionTitle === section.sectionTitle
        )
      );

      combinations = combinations.filter(combination => {
        if (!isGuestCombination(combination)) return false;

        return filteredSections.some((section: ISectionAll) => {
          return (
            section.sectionTitle === combination.sectionTitle &&
            ((sectionType === RADIO_OPTIONS.OPERATION &&
              section.sectionType === "Operation") ||
              (sectionType === RADIO_OPTIONS.SYSTEM &&
                section.sectionType === "System"))
          );
        });
      });
    }
  }

  // Apply filters based on provided IDs
  const filteredCombinations = combinations.filter(
    (combination: ICombinationAll) => {
      if (isCombination(combination)) {
        return (
          (!clientType?.value ||
            combination?.clientType?.ID === clientType?.value) &&
          (!questionType?.value ||
            combination.questionType?.ID === questionType?.value) &&
          (!section?.value || combination.section?.ID === section?.value)
        );
      } else {
        //Filter on titles in Logistikportalen env
        return (
          (!clientType?.label ||
            combination.clientTypeTitle === clientType?.label) &&
          (!questionType?.label ||
            combination.questionTypeTitle === questionType?.label) &&
          (!section?.label || combination.sectionTitle === section?.label)
        );
      }
    }
  );

  // Extract unique IDs or labels
  const uniqueSectionIds = [
    ...new Set(
      filteredCombinations.map((combination: ICombinationAll) =>
        isGuestCombination(combination)
          ? combination.sectionTitle
          : combination.section?.ID
      )
    )
  ];

  const uniqueQuestionTypeIds = [
    ...new Set(
      filteredCombinations.map((combination: ICombinationAll) =>
        isGuestCombination(combination)
          ? combination.questionTypeTitle
          : combination.questionType?.ID
      )
    )
  ];
  const uniqueClientTypeIds = [
    ...new Set(
      filteredCombinations.map((combination: ICombinationAll) =>
        isGuestCombination(combination)
          ? combination.clientTypeTitle
          : combination.clientType?.ID
      )
    )
  ];

  const seenSectionTitles = new Set();

  const newSectionOptions = filteredSections
    .filter((section: ISectionAll) =>
      uniqueSectionIds.includes(
        data.isLoggedIn ? section.ID : section.sectionTitle
      )
    )
    .filter((section: ISectionAll) => {
      const normalizedTitle = section.sectionTitle?.trim().toLowerCase();
      if (seenSectionTitles.has(normalizedTitle)) return false;
      seenSectionTitles.add(normalizedTitle);
      return true;
    })
    .map((section: ISectionAll) => ({
      value: section.ID,
      label: section.sectionTitle
    }));

  const newQuestionTypeOptions = data.questionTypes
    .filter(
      (questionType: IQuestionType) =>
        questionType.questionTitle !== "Internuppdrag" &&
        uniqueQuestionTypeIds.includes(
          data.isLoggedIn ? questionType.ID : questionType.questionTitle
        )
    )
    .map((questionType: IQuestionType) => ({
      value: questionType.ID,
      label: questionType.questionTitle
    }));

  const newClientTypeOptions = data.clientTypes
    .filter((clientType: IClientType) =>
      uniqueClientTypeIds.includes(
        data.isLoggedIn ? clientType.ID : clientType.clientTypeTitle
      )
    )
    .map((clientType: IClientType) => ({
      value: clientType.ID,
      label: clientType.clientTypeTitle
    }));

  return [
    newSectionOptions.sort(
      (a: valueLabelPair<number>, b: valueLabelPair<number>) =>
        a.label.localeCompare(b.label.trim(), "sv", {
          sensitivity: "base"
        })
    ),
    newQuestionTypeOptions.sort(
      (a: valueLabelPair<number>, b: valueLabelPair<number>) =>
        a.label.localeCompare(b.label.trim(), "sv", {
          sensitivity: "base"
        })
    ),
    newClientTypeOptions.sort(
      (a: valueLabelPair<number>, b: valueLabelPair<number>) =>
        a.label.localeCompare(b.label.trim(), "sv", {
          sensitivity: "base"
        })
    )
  ];
}
